// @ts-nocheck
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { uniqBy, isEqual } from 'lodash';
import { useSelector } from 'react-redux';
import moment from 'moment';

import { createFormSchema, createInitialFormValues } from '../utils/formUtils';
import { getVisibleAttributes } from '../utils/conditionalRuleEvaluator';
import swalToast from '../components/Common/swalToast';
import useAuth from './Auth/useAuth';
import {
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  NOOP,
  YYYY_MM_DD_DATE_FORMAT,
} from '../const/CommonConst';
import { filterSectionsByPermissions, isValueEmpty } from '../utils/Utils';

/**
 * Filters validation errors to only include errors for visible attributes
 * @param {Object} errors - Validation errors object
 * @param {Array} allAttributes - All form attributes
 * @param {Object} formValues - Current form values
 * @param {Object} attributeDetails - Original attribute values
 * @param {Object} syncAttributes - Synced attribute values
 * @returns {Object} - Filtered errors object
 */
const filterValidationErrors = (
  errors,
  allAttributes,
  formValues,
  attributeDetails,
  syncAttributes
) => {
  // Create complete form values for conditional evaluation
  const allFormValues = {
    ...attributeDetails,
    ...syncAttributes,
    ...formValues,
  };

  // Get visible attributes
  const { visibleAttributes } = getVisibleAttributes(
    allAttributes,
    allFormValues
  );
  const visibleAttributeIds = new Set(
    visibleAttributes.map((attr) => attr?.id)
  );

  // Filter errors to only include visible attributes
  const filteredErrors = {};
  Object.keys(errors || {}).forEach((attributeId) => {
    const numericId = Number(attributeId);
    if (visibleAttributeIds.has(numericId)) {
      filteredErrors[attributeId] = errors[attributeId];
    }
  });

  return filteredErrors;
};

/**
 * Transform form values for backend payload
 * @param {Object} values - Form values from Formik
 * @param {Array} allAttributes - Array of all attributes with metadata
 * @param {Object} originalValues - Original attribute values for comparison
 * @param {Array} userList - List of users for User type attribute transformation
 * @param {Boolean} skipUnchangedValueCheck - Skip removing unchanged attributes from payload
 * @returns {Object} - Transformed values ready for backend
 */
const transformFormValues = (
  values,
  allAttributes,
  originalValues = EMPTY_OBJECT,
  userList = EMPTY_ARRAY,
  skipUnchangedValueCheck = false
) => {
  const transformedValues = { ...values };

  allAttributes?.forEach((attribute) => {
    const attributeId = attribute?.id;
    const attributeValue = transformedValues[attributeId];
    const originalValue = originalValues[attributeId];

    if (
      attribute?.is_editable === false &&
      attribute?.attribute_origin === 'common'
    ) {
      // Remove non-editable and common origin attributes from the payload entirely as their values will not be changed
      delete transformedValues[attributeId];
    } else if (
      !skipUnchangedValueCheck &&
      isEqual(attributeValue, originalValue)
    ) {
      // Remove unchanged attributes from the payload to optimize network transfer
      delete transformedValues[attributeId];
    } else if (
      attribute?.data_type === 'Document' &&
      attributeValue &&
      Array.isArray(attributeValue)
    ) {
      // Transform Document type attributes from objects to array of document IDs
      transformedValues[attributeId] = attributeValue.map(
        (doc) => doc?.document_id
      );
    } else if (attribute?.data_type === 'Association') {
      // for association type attribute send entity_id and entity_type, as BE is expecting those fields for association type attribute
      // attributeValue?.entity_id checking this empty because, we have to send the null to BE if no value selected
      transformedValues[attributeId] = !isValueEmpty(attributeValue?.entity_id)
        ? {
            entity_id: attributeValue?.entity_id,
            entity_type: attributeValue?.entity_type,
          }
        : null;
    } else if (attribute?.data_type === 'User') {
      // transform username into user_id, as BE is expecting id
      transformedValues[attributeId] =
        userList?.find((user) => user?.username === attributeValue)?.user_id ??
        attributeValue;
    } else if (attribute?.data_type === 'AttributeList') {
      transformedValues[attributeId] = !isValueEmpty(attributeValue?.id)
        ? attributeValue?.id
        : null;
    } else if (attribute?.data_type === 'ParameterTableList') {
      transformedValues[attributeId] = !isValueEmpty(attributeValue?.id)
        ? attributeValue?.id
        : null;
    } else if (attribute?.data_type === 'EmailTemplateList') {
      transformedValues[attributeId] = !isValueEmpty(
        attributeValue?.email_template_id
      )
        ? attributeValue?.email_template_id
        : null;
    } else if (attribute?.data_type === 'Date') {
      transformedValues[attributeId] = attributeValue
        ? moment(attributeValue).format(YYYY_MM_DD_DATE_FORMAT)
        : null;
    }
    // All other editable and changed attributes remain unchanged
  });

  // Remove attributes that exist in form values but are not present in allAttributes, in get_attributes_data API we are getting values of attributes which are not added into the template and we will not edit those attributes in any case.
  const validAttributeIds = new Set(allAttributes?.map((attr) => attr?.id));
  Object.keys(transformedValues).forEach((key) => {
    // Convert string key to number for comparison with validAttributeIds Set
    const keyAsNumber = Number(key);
    if (!validAttributeIds.has(keyAsNumber)) {
      delete transformedValues[key];
    }
  });

  return transformedValues;
};

/**
 * Custom hook for managing entity details form with multi-edit functionality
 * @param {Object} params - Hook parameters
 * @param {Object} params.formDetails - Form configuration with attributes and sections
 * @param {Object} params.attributeDetails - Current attribute values
 * @param {string|number} params.entityId - Entity ID for the form
 * @param {string} params.entityType - Type of entity (e.g., 'ModelInventory')
 * @param {Function} params.updateEntityCallback - Function to update entity
 * @param {Function} params.onSuccess - Callback function on successful form submission
 * @param {string} params.successMessage - Success message to display
 * @returns {Object} - Hook return object with form state and handlers
 */
const useEntityDetailsForm = ({
  formDetails,
  attributeDetails,
  entityId,
  entityType,
  updateEntityCallback,
  onSuccess,
  handleGetAttributesData = NOOP,
  successMessage = 'Details updated successfully.',
  syncAttributes,
  getDataWithoutApiCall = false,
  skipUnchangedValueCheck = false,
  entitySelectedGroupId = null,
  selectedStateNode = null,
  isNodeEdit = false,
}) => {
  const { getAllUsers } = useAuth();
  const [initialFormValues, setInitialFormValues] = useState({});
  const [formSchema, setFormSchema] = useState({});
  const [isMultipleEditMode, setIsMultipleEditMode] = useState(false);
  // not a proper way to get userList, temporary solution, will get fixed in template verisioning story
  // it should be coming from a centralized state
  const [userList, setUserList] = useState([]);

  const entityKey = `${entityType}_${entityId}`;
  const entityCurrentGroupId = useSelector(
    (state) =>
      state?.common?.workflowGroupsVar?.[entityKey]?.entityCurrentGroupId
  );

  const sectionPermissions = useSelector(
    (state) => state?.common?.sectionPermissions?.[entityKey]
  );

  const frontendConfig = useSelector((state) => state?.tenant?.frontendConfig);

  const filteredSections = useMemo(() => {
    return filterSectionsByPermissions({
      sections: formDetails?.sections,
      sectionPermissions,
      entityCurrentGroupId,
      entitySelectedGroupId,
      selectedStateNode,
      isNodeEdit,
    });
  }, [
    formDetails?.sections,
    sectionPermissions,
    entityCurrentGroupId,
    entitySelectedGroupId,
    selectedStateNode,
    isNodeEdit,
  ]);

  // Memoized calculation of all attributes to be used throughout the component
  const allAttributes = useMemo(() => {
    const basicAttributes = formDetails?.attributes || [];
    const sectionedAttributes = filteredSections?.reduce((acc, section) => {
      section?.attributes?.forEach((attribute) => {
        acc.push(attribute);
      });
      return acc;
    }, []);

    // Create a merged list of all attributes with no duplicates based on id
    return uniqBy([...basicAttributes, ...(sectionedAttributes || [])], 'id');
  }, [formDetails, filteredSections]);

  const generateInitialValuesAndSchema = useCallback(() => {
    const schema = createFormSchema(allAttributes, {
      defaultTextMaxLength: frontendConfig?.defaultTextMaxLength,
    });

    // Merge syncAttributes into attributeDetails so synced values persist
    const mergedAttributeDetails = {
      ...attributeDetails,
      ...(syncAttributes || {}),
    };

    const newInitialValues = createInitialFormValues(
      allAttributes,
      mergedAttributeDetails
    );

    setFormSchema(schema);
    setInitialFormValues(newInitialValues);
  }, [allAttributes, attributeDetails, syncAttributes, frontendConfig]);
  const updateInitialValuesFromAPI = useCallback(
    (newValues) => {
      setInitialFormValues(newValues);
    },
    [setInitialFormValues]
  );

  const getUserDetails = () => {
    if (userList.length > 0) return;
    getAllUsers({}, false).then((res) => {
      if (res?.data) {
        setUserList(res.data);
      }
    });
  };

  useEffect(() => {
    getUserDetails();
  }, []);

  const entityDetailsFormikForm = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema: formSchema,
    onSubmit: (values, { setSubmitting }) => {
      // Transform form values using the dedicated function
      // Merge values with syncAttributes before transform
      const mergedValues = {
        ...values,
        ...(syncAttributes || {}),
      };

      const transformedValues = transformFormValues(
        mergedValues,
        allAttributes,
        attributeDetails,
        userList,
        skipUnchangedValueCheck
      );

      const payload = {
        entity_id: entityId,
        entity_type: entityType,
        ...transformedValues,
      };

      updateEntityCallback(payload)
        .then((res) => {
          if (res) {
            swalToast({
              icon: 'success',
              title: successMessage,
              id: 'entity-update-success',
            });
            setIsMultipleEditMode(false);
            if (onSuccess) {
              onSuccess();
            }
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });

  const handleCancelEditForm = useCallback(() => {
    setIsMultipleEditMode(false);
    entityDetailsFormikForm.resetForm();
    handleGetAttributesData();
  }, [setIsMultipleEditMode, entityDetailsFormikForm]);

  const handleEditForm = useCallback(() => {
    setIsMultipleEditMode(true);
  }, [setIsMultipleEditMode]);

  const validateFilteredFormikErrors = useCallback(
    () =>
      // First validate the form using standard formik validation
      // Filter errors to only include visible attributes
      entityDetailsFormikForm
        .validateForm()
        .then((errors) =>
          filterValidationErrors(
            errors,
            allAttributes,
            entityDetailsFormikForm.values,
            attributeDetails,
            syncAttributes
          )
        ),
    [entityDetailsFormikForm, allAttributes, attributeDetails, syncAttributes]
  );

  const handleSaveForm = useCallback(async () => {
    const filteredErrors = await validateFilteredFormikErrors();

    if (Object.keys(filteredErrors || {})?.length > 0) {
      // Set filtered errors to show user only relevant errors
      entityDetailsFormikForm.setErrors(filteredErrors);
      entityDetailsFormikForm.setTouched(
        Object.keys(filteredErrors)?.reduce((touched, field) => {
          return { ...touched, [field]: true };
        }, {})
      );
      return; // Don't proceed with submission
    }
    // No validation errors for visible fields, proceed with direct submission
    // Set submitting state manually
    entityDetailsFormikForm.setSubmitting(true);

    // Call onSubmit directly with current values and form actions
    const mockFormActions = {
      setSubmitting: (isSubmitting) => {
        entityDetailsFormikForm.setSubmitting(isSubmitting);
      },
      setErrors: (formErrors) => {
        entityDetailsFormikForm.setErrors(formErrors);
      },
      setTouched: (touchedFields) => {
        entityDetailsFormikForm.setTouched(touchedFields);
      },
    };

    // Get the onSubmit function and call it directly
    const formConfig = {
      onSubmit: (values, { setSubmitting }) => {
        // Transform form values using the dedicated function
        // Merge values with syncAttributes before transform
        const mergedValues = {
          ...values,
          ...(syncAttributes || {}),
        };

        const transformedValues = transformFormValues(
          mergedValues,
          allAttributes,
          attributeDetails,
          userList,
          skipUnchangedValueCheck
        );

        const payload = {
          entity_id: entityId,
          entity_type: entityType,
          ...transformedValues,
        };

        updateEntityCallback(payload)
          .then((res) => {
            if (res) {
              swalToast({
                icon: 'success',
                title: successMessage,
                id: 'entity-update-success',
              });
              setIsMultipleEditMode(false);
              if (onSuccess) {
                onSuccess();
              }
            }
          })
          .finally(() => {
            setSubmitting(false);
          });
      },
    };

    // Call the onSubmit function directly
    formConfig.onSubmit(entityDetailsFormikForm?.values, mockFormActions);
  }, [
    entityDetailsFormikForm,
    allAttributes,
    attributeDetails,
    syncAttributes,
    entityId,
    entityType,
    updateEntityCallback,
    successMessage,
    setIsMultipleEditMode,
    onSuccess,
    validateFilteredFormikErrors,
    userList,
  ]);

  const handleSaveFormPromise = useCallback(
    (options = {}) => {
      return new Promise((resolve, reject) => {
        (async () => {
          const { skipValidation = false } = options;
          const filteredErrors = skipValidation
            ? {}
            : await validateFilteredFormikErrors();

          if (Object.keys(filteredErrors).length > 0) {
            // Set filtered errors and reject the promise
            entityDetailsFormikForm.setErrors(filteredErrors);
            entityDetailsFormikForm.setTouched(
              Object.keys(filteredErrors)?.reduce((touched, field) => {
                return { ...touched, [field]: true };
              }, {})
            );
            reject(new Error('Form validation failed'));
            return;
          }
          // Validation passed - proceed with form submission
          const values = entityDetailsFormikForm?.values;

          // Merge values with syncAttributes before transform
          const mergedValues = {
            ...values,
            ...(syncAttributes || {}),
          };

          // Transform form values for backend
          const transformedValues = transformFormValues(
            mergedValues,
            allAttributes,
            attributeDetails,
            userList,
            skipUnchangedValueCheck
          );

          // Prepare payload for API call
          const payload = {
            entity_id: entityId,
            entity_type: entityType,
            ...transformedValues,
          };
          if (getDataWithoutApiCall) {
            return payload;
          }
          // Make the API call
          updateEntityCallback(payload)
            .then((response) => {
              if (response) {
                // API call successful
                swalToast({
                  icon: 'success',
                  title: successMessage,
                  id: 'entity-update-success',
                });
                handleCancelEditForm();
                if (onSuccess) {
                  onSuccess();
                }
                resolve(response);
              }
            })
            .catch((apiError) => {
              // Reject the promise so the calling code knows the save failed
              reject(apiError);
            });
        })();
      });
    },
    [
      entityDetailsFormikForm,
      allAttributes,
      attributeDetails,
      syncAttributes,
      entityId,
      entityType,
      updateEntityCallback,
      successMessage,
      handleCancelEditForm,
      onSuccess,
      validateFilteredFormikErrors,
      userList,
    ]
  );

  const handleSaveOtherDetailsForm = useCallback(async () => {
    return new Promise((resolve, reject) => {
      (async () => {
        const filteredErrors = await validateFilteredFormikErrors();

        if (Object.keys(filteredErrors).length > 0) {
          // Set filtered errors and reject the promise
          entityDetailsFormikForm.setErrors(filteredErrors);
          entityDetailsFormikForm.setTouched(
            Object.keys(filteredErrors)?.reduce((touched, field) => {
              return { ...touched, [field]: true };
            }, {})
          );
          reject(new Error('Form validation failed'));
          return;
        }
        // Validation passed - proceed with form submission
        const values = entityDetailsFormikForm?.values;

        // Merge values with syncAttributes before transform
        const mergedValues = {
          ...values,
          ...(syncAttributes || {}),
        };

        // Transform form values for backend
        const transformedValues = transformFormValues(
          mergedValues,
          allAttributes,
          attributeDetails,
          userList,
          skipUnchangedValueCheck
        );

        // Prepare payload for API call
        const payload = {
          ...transformedValues,
        };

        resolve(payload);
      })();
    });
  }, [
    entityDetailsFormikForm,
    allAttributes,
    attributeDetails,
    syncAttributes,
    entityId,
    entityType,
    validateFilteredFormikErrors,
    userList,
  ]);

  // Effect to regenerate form values and schema when dependencies change
  useEffect(() => {
    generateInitialValuesAndSchema();
  }, [generateInitialValuesAndSchema]);

  // Re-validate form when schema changes dynamically (e.g., date constraints)
  const isInitialSchemaRef = useRef(true);
  useEffect(() => {
    if (isInitialSchemaRef.current) {
      isInitialSchemaRef.current = false;
      return;
    }
    // Only re-validate if there are touched fields
    if (Object.keys(entityDetailsFormikForm?.touched || {})?.length > 0) {
      entityDetailsFormikForm?.validateForm();
    }
  }, [formSchema]);

  return {
    // Form state
    isMultipleEditMode,
    initialFormValues,
    formSchema,
    allAttributes,
    setIsMultipleEditMode,
    setFormSchema,

    // Formik instance
    entityDetailsFormikForm,
    updateInitialValuesFromAPI,
    // Handlers
    handleEditForm,
    handleSaveForm,
    handleCancelEditForm,

    // Utilities
    generateInitialValuesAndSchema,
    handleSaveFormPromise,
    handleSaveOtherDetailsForm,

    isSubmitting: entityDetailsFormikForm?.isSubmitting,
    validateFilteredFormikErrors,
    allUserList: userList,
  };
};

export default useEntityDetailsForm;
